import { z } from 'zod'
import { deckClosesInMinutesSchema, deckItemsInputSchema, deckTitleInputSchema } from './deck'
import { reportReasonValues } from './report'

/**
 * Lifetime the backend signs chat attachment URLs for. A client may treat any URL
 * from a payload older than this as certainly dead — the signature can already be
 * up to a third of this old when it is served, so the reverse does not hold and a
 * younger payload still needs the load to prove it.
 */
export const ATTACHMENT_URL_TTL_SECONDS = 3 * 60 * 60

/** Every message kind this build knows how to render. */
export const messageKindSchema = z.enum([
  'text',
  'system',
  'image',
  'video',
  'voice',
  'event_share',
  'listing_share',
  'plan_share',
  'plan_invite',
  'location',
  'spot_ping',
  'plan_vote',
  'swipe_deck',
  'drop_share',
  'drop_voucher',
  'room_hi',
  'file',
])

export type MessageKind = z.infer<typeof messageKindSchema>

/** Compact quote of the message a reply points at; null when the parent was deleted. */
export const messageReplyPreviewSchema = z.object({
  id: z.string().uuid(),
  senderName: z.string().nullable(),
  /** Deliberately not `messageKindSchema`: the backend may ship a kind an older
   * app predates, and a strict enum here would reject the whole message rather
   * than let the client fall back to a generic label. */
  kind: z.string(),
  /** Short text preview, or an English kind label like "Photo" / "Event" —
   * clients relabel non-text kinds from `kind` rather than showing this. */
  preview: z.string(),
})

export type MessageReplyPreview = z.infer<typeof messageReplyPreviewSchema>

/**
 * Denormalized quote of a message that lives in ANOTHER conversation, captured at
 * send time ("reply privately" from a group into a 1:1).
 *
 * Deliberately distinct from `replyTo`: that one is a same-conversation pointer
 * resolved by join on every read, so it tracks edits and vanishes on delete. This
 * one is a point-in-time copy carrying only a display name and a plain-text
 * preview — no message/conversation ids — so the original can never be joined back
 * to, and later edits or deletions in the source conversation cannot retroactively
 * change or leak anything through it.
 */
export const messageQuoteSnapshotSchema = z.object({
  senderName: z.string().min(1).max(120),
  /** Plain single-line preview; clamped server-side before it is stored. */
  preview: z.string().min(1).max(500),
  /** A status quoted from the row above Chats: `senderName` is whose status, labelled per reader. */
  source: z.literal('status').optional(),
})

export type MessageQuoteSnapshot = z.infer<typeof messageQuoteSnapshotSchema>

/** One emoji reaction bucket on a message, aggregated for the viewer. */
export const messageReactionSchema = z.object({
  emoji: z.string(),
  count: z.number().int(),
  /** Whether the current viewer is one of the reactors with this emoji. */
  mine: z.boolean(),
})

export type MessageReaction = z.infer<typeof messageReactionSchema>

/**
 * One resolved @mention on a message: the mentioned person's id, handle and name.
 * `username` is null for people tagged by id (no `@handle` token in the body).
 */
export const messageMentionSchema = z.object({
  userId: z.string().uuid(),
  username: z.string().nullable(),
  displayName: z.string().nullable().optional(),
})

export type MessageMention = z.infer<typeof messageMentionSchema>

/** Aggregate of the 👍/👎 responses to an in-chat spot-ping message, for the viewer. */
export const spotPingResponseAggregateSchema = z.object({
  yes: z.number().int(),
  no: z.number().int(),
  /** The viewer's own recorded answer, or null when they haven't responded. */
  mine: z.enum(['yes', 'no']).nullable(),
})

export type SpotPingResponseAggregate = z.infer<typeof spotPingResponseAggregateSchema>

/** Set (`yes`/`no`) or clear (null) the viewer's response to a spot-ping message. */
export const spotPingResponseInputSchema = z.object({
  answer: z.enum(['yes', 'no']).nullable(),
})

export type SpotPingResponseInput = z.infer<typeof spotPingResponseInputSchema>

export const dmMessageDtoSchema = z.object({
  id: z.string().uuid(),
  /** Stable client-side key so an optimistic message doesn't re-key (blink) on send. */
  clientId: z.string().optional(),
  conversationId: z.string().uuid(),
  senderId: z.string().uuid().nullable(),
  senderName: z.string().nullable(),
  senderAvatarUrl: z.string().nullable(),
  kind: messageKindSchema,
  content: z.string(),
  /** Text the sender actually wrote on an attachment, absent when they wrote none.
   * `content` carries a kind label for clients too old to render the kind, so a
   * client that renders it natively must caption from here instead. */
  caption: z.string().nullable().optional(),
  /** Signed URL of the compressed rendition (max 1600px edge) — what the bubble
   * renders. Never the untouched original: decoding a multi-megapixel file for a
   * 280pt bubble blanks the preview on Android. */
  imageUrl: z.string().nullable(),
  /** Signed URL of the untouched original, present only while a full-quality
   * send is inside its 48h retention. The full-screen viewer opens this. */
  imageFullUrl: z.string().nullable().optional(),
  imageWidth: z.number().int().nullable(),
  imageHeight: z.number().int().nullable(),
  /** Signed URL of a video message's clip (kind `video`), else null. Optional so
   * existing optimistic-message builders stay valid. */
  videoUrl: z.string().nullable().optional(),
  /** Signed URL of a video message's poster frame (kind `video`), else null. Optional
   * so existing optimistic-message builders stay valid. */
  videoThumbnailUrl: z.string().nullable().optional(),
  /** Pixel width of a `video` message's clip, for the bubble's aspect ratio. Optional
   * so existing optimistic-message builders stay valid. */
  videoWidth: z.number().int().nullable().optional(),
  /** Pixel height of a `video` message's clip, for the bubble's aspect ratio. Optional
   * so existing optimistic-message builders stay valid. */
  videoHeight: z.number().int().nullable().optional(),
  /** Signed URL of a voice message's audio (kind `voice`), else null. Optional so
   * existing optimistic-message builders stay valid. */
  audioUrl: z.string().nullable().optional(),
  /** Voice-message or video-clip duration in milliseconds. Optional so existing
   * optimistic-message builders stay valid. */
  durationMs: z.number().int().nullable().optional(),
  /** Normalized 0–1 amplitude samples for the voice waveform, or null. Optional so
   * existing optimistic-message builders stay valid. */
  waveform: z.array(z.number()).nullable().optional(),
  /** Signed URL of a file message's document (kind `file`), else null. Optional so
   * existing optimistic-message builders stay valid. */
  fileUrl: z.string().nullable().optional(),
  /** Original file name of a `file` message, for the bubble label. Optional so
   * existing optimistic-message builders stay valid. */
  fileName: z.string().nullable().optional(),
  /** MIME type of a `file` message's document. Optional so existing
   * optimistic-message builders stay valid. */
  mimeType: z.string().nullable().optional(),
  /** Byte size of a `file` message's document. Optional so existing
   * optimistic-message builders stay valid. */
  sizeBytes: z.number().int().nullable().optional(),
  /** True once the 48h attachment retention deleted the stored document or video
   * clip — the bubble shows an "no longer available" state. Optional so existing
   * optimistic-message builders stay valid. */
  attachmentExpired: z.boolean().optional(),
  /** True when an image was sent at original quality (its original is retained for
   * 48h, then the bubble degrades to the compressed copy). Optional so existing
   * optimistic-message builders stay valid. */
  fullQuality: z.boolean().optional(),
  /** When the 48h attachment retention reclaims this message's stored original;
   * null once reclaimed or when nothing is retained. Optional so existing
   * optimistic-message builders stay valid. */
  attachmentExpiresAt: z.string().nullable().optional(),
  viewOnce: z.boolean(),
  viewed: z.boolean(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
  /** When a disappearing message self-deletes; null for permanent messages. */
  expiresAt: z.string().nullable(),
  /** Set when the sender edited the message (WhatsApp "Edited" label); null otherwise.
   * Optional so existing optimistic-message builders stay valid. */
  editedAt: z.string().nullable().optional(),
  /** Set when the message was deleted for everyone; `content`/`imageUrl`/`metadata` are
   * nulled server-side and the client renders a "message deleted" tombstone.
   * Optional so existing optimistic-message builders stay valid. */
  deletedAt: z.string().nullable().optional(),
  /** Compact quote of the replied-to message, or null when this isn't a reply /
   * the parent was deleted. Optional so existing optimistic-message builders stay valid. */
  replyTo: messageReplyPreviewSchema.nullable().optional(),
  /** Point-in-time quote of a message from another conversation (see
   * `messageQuoteSnapshotSchema`), or null. Optional so existing optimistic-message
   * builders stay valid. */
  quotedFrom: messageQuoteSnapshotSchema.nullable().optional(),
  /** Per-emoji reaction buckets aggregated for the viewer. Defaults to [] server-side;
   * optional so existing optimistic-message builders stay valid. */
  reactions: z.array(messageReactionSchema).optional(),
  /** True when this message was forwarded (WhatsApp "Forwarded" label). Optional so
   * existing optimistic-message builders stay valid. */
  forwarded: z.boolean().optional(),
  /** Resolved @mentions in the body (handle → user id), so the bubble highlights and
   * links them. Optional so existing optimistic-message builders stay valid. */
  mentions: z.array(messageMentionSchema).optional(),
  /** Set when this message is pinned in its conversation (WhatsApp pin); null otherwise.
   * Optional so existing optimistic-message builders stay valid. */
  pinnedAt: z.string().nullable().optional(),
  /** Whether the current viewer has personally starred (bookmarked) this message.
   * Optional so existing optimistic-message builders stay valid. */
  starred: z.boolean().optional(),
  /** For a `spot_ping` message: the aggregated 👍/👎 responses (with the viewer's own
   * answer), else null. Optional so existing optimistic-message builders stay valid. */
  spotPingResponses: spotPingResponseAggregateSchema.nullable().optional(),
})

export type DmMessageDto = z.infer<typeof dmMessageDtoSchema>

/** The pinned messages of a conversation (most recent pin first). */
export const conversationPinsResponseSchema = z.object({
  messages: z.array(dmMessageDtoSchema),
})

export type ConversationPinsResponse = z.infer<typeof conversationPinsResponseSchema>

/** One of the viewer's starred messages, tagged with where it lives so the
 * starred-messages screen can jump back to the conversation. */
export const starredMessageDtoSchema = dmMessageDtoSchema.extend({
  conversationType: z.enum(['direct', 'group']),
  /** Group title, or the other participant's name for a 1:1 — a label for the row. */
  conversationName: z.string().nullable(),
})

export type StarredMessageDto = z.infer<typeof starredMessageDtoSchema>

/** The viewer's starred messages across all their conversations, newest star first. */
export const starredMessagesResponseSchema = z.object({
  messages: z.array(starredMessageDtoSchema),
})

export type StarredMessagesResponse = z.infer<typeof starredMessagesResponseSchema>

export const editMessageInputSchema = z.object({
  body: z.string().min(1).max(2000),
})

export type EditMessageInput = z.infer<typeof editMessageInputSchema>

export const deleteMessageInputSchema = z.object({
  scope: z.enum(['everyone', 'me']),
})

export type DeleteMessageInput = z.infer<typeof deleteMessageInputSchema>

export const dmMessagesResponseSchema = z.object({
  messages: z.array(dmMessageDtoSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type DmMessagesResponse = z.infer<typeof dmMessagesResponseSchema>

/** One in-conversation message-search hit: the message id, a highlightable snippet, and its timestamp. */
export const messageSearchResultSchema = z.object({
  id: z.string().uuid(),
  /** Text window around the match (with ellipses), for the result row. */
  snippet: z.string(),
  createdAt: z.string(),
})

export type MessageSearchResult = z.infer<typeof messageSearchResultSchema>

export const messageSearchResponseSchema = z.object({
  results: z.array(messageSearchResultSchema),
})

export type MessageSearchResponse = z.infer<typeof messageSearchResponseSchema>

const sendDmKindSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('text'),
    content: z.string().min(1).max(2000),
    /** Cross-conversation quote to carry along with the message, stored as a snapshot. */
    quotedFrom: messageQuoteSnapshotSchema.optional(),
  }),
  z.object({
    kind: z.literal('image'),
    imagePath: z.string(),
    width: z.number().int(),
    height: z.number().int(),
    caption: z.string().max(2000).optional(),
    viewOnce: z.boolean().optional(),
    /** Storage path of the uncompressed original (full-quality send); retained 48h. */
    originalPath: z.string().optional(),
    fullQuality: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('video'),
    videoPath: z.string(),
    /** Storage path of the client-generated poster frame shown before playback. */
    thumbnailPath: z.string(),
    width: z.number().int(),
    height: z.number().int(),
    durationMs: z.number().int().min(0),
    caption: z.string().max(2000).optional(),
  }),
  z.object({
    kind: z.literal('file'),
    filePath: z.string(),
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(255),
    sizeBytes: z.number().int().min(0),
  }),
  z.object({
    kind: z.literal('voice'),
    audioPath: z.string(),
    durationMs: z.number().int().min(0),
    waveform: z.array(z.number()).max(256).optional(),
  }),
  z.object({ kind: z.literal('event_share'), eventId: z.string().uuid() }),
  // Exactly one of eventId (new card, plan created behind it) or planId
  // (card for an existing plan, e.g. inviting one more person from a 1:1).
  // Enforced server-side: discriminatedUnion cannot wrap a refined object.
  z.object({
    kind: z.literal('plan_vote'),
    eventId: z.string().uuid().optional(),
    planId: z.string().uuid().optional(),
  }),
  z.object({
    kind: z.literal('swipe_deck'),
    items: deckItemsInputSchema,
    title: deckTitleInputSchema.optional(),
    closesInMinutes: deckClosesInMinutesSchema.optional(),
    allowOthersToAdd: z.boolean().optional(),
  }),
  z.object({ kind: z.literal('listing_share'), listingId: z.string().uuid() }),
  z.object({ kind: z.literal('plan_share'), planId: z.string().uuid() }),
  z.object({ kind: z.literal('plan_invite'), planId: z.string().uuid() }),
  z.object({ kind: z.literal('spot_ping'), placeId: z.string().uuid() }),
  z.object({
    kind: z.literal('location'),
    lat: z.number(),
    lng: z.number(),
    label: z.string().max(200).optional(),
    address: z.string().max(500).optional(),
  }),
])

/** Optional reply target, valid on every message kind (WhatsApp quotes any bubble). */
export const sendDmInputSchema = z.intersection(
  sendDmKindSchema,
  z.object({ replyToMessageId: z.string().uuid().optional() }),
)

export type SendDmInput = z.infer<typeof sendDmInputSchema>

/** Set (emoji) or clear (null) the viewer's single reaction on a message. */
export const reactMessageInputSchema = z.object({
  emoji: z.string().min(1).max(32).nullable(),
})

export type ReactMessageInput = z.infer<typeof reactMessageInputSchema>

/** Copy a message into one or more conversations the caller participates in. */
export const forwardMessageInputSchema = z
  .object({
    targetConversationIds: z.array(z.string().uuid()).max(20).default([]),
    /** Plan chats, addressed by plan id. */
    targetPlanIds: z.array(z.string().uuid()).max(20).default([]),
  })
  .refine((input) => input.targetConversationIds.length + input.targetPlanIds.length > 0, {
    message: 'Pick at least one chat',
  })

export type ForwardMessageInput = z.infer<typeof forwardMessageInputSchema>

/** Flag a chat message for moderation review. */
export const reportMessageInputSchema = z.object({
  reason: z.enum(reportReasonValues),
  details: z.string().max(500).optional(),
})

export type ReportMessageInput = z.infer<typeof reportMessageInputSchema>

/** The message ids created by a forward, one per target conversation. */
export const forwardMessageResponseSchema = z.object({
  messageIds: z.array(z.string().uuid()),
})

export type ForwardMessageResponse = z.infer<typeof forwardMessageResponseSchema>

export const sendDmResponseSchema = z.object({
  message: dmMessageDtoSchema,
})

export type SendDmResponse = z.infer<typeof sendDmResponseSchema>

/** One image in a conversation's shared-media grid (1:1 or group). */
export const conversationMediaItemSchema = z.object({
  messageId: z.string().uuid(),
  imageUrl: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  createdAt: z.string(),
})

export type ConversationMediaItem = z.infer<typeof conversationMediaItemSchema>

/** Paginated shared-media page for a conversation, newest first. */
export const conversationMediaResponseSchema = z.object({
  items: z.array(conversationMediaItemSchema),
  nextCursor: z.string().nullable(),
})

export type ConversationMediaResponse = z.infer<typeof conversationMediaResponseSchema>

export const dmAttachmentResponseSchema = z.object({
  path: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  /** Set on full-quality uploads: the stored uncompressed original's path. */
  originalPath: z.string().optional(),
})

export type DmAttachmentResponse = z.infer<typeof dmAttachmentResponseSchema>

/** Storage descriptor of an uploaded chat document attachment. */
export const dmFileAttachmentResponseSchema = z.object({
  path: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
})

export type DmFileAttachmentResponse = z.infer<typeof dmFileAttachmentResponseSchema>

/** Storage path of an uploaded voice-message audio file. */
export const dmAudioAttachmentResponseSchema = z.object({
  path: z.string(),
})

export type DmAudioAttachmentResponse = z.infer<typeof dmAudioAttachmentResponseSchema>

/** Storage descriptor of an uploaded chat video clip (retained 48h after send). */
export const dmVideoAttachmentResponseSchema = z.object({
  path: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
})

export type DmVideoAttachmentResponse = z.infer<typeof dmVideoAttachmentResponseSchema>

export const profileBadgeValues = ['official', 'verified_venue', 'verified_organizer'] as const

export const dmParticipantDtoSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  badge: z.enum(profileBadgeValues).nullable().optional(),
  /** @handle, for @mention autocomplete; null when the user hasn't picked one. */
  username: z.string().nullable().optional(),
})

export type DmParticipantDto = z.infer<typeof dmParticipantDtoSchema>

/** Structured payload of a `system` message (from `metadata.system`), so the
 * client can localize an event ("X created the plan") instead of showing the
 * server's fallback English `body`. */
export const dmSystemPayloadSchema = z.object({
  kind: z.string(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  targetId: z.string().nullable(),
  targetName: z.string().nullable(),
})

export type DmSystemPayload = z.infer<typeof dmSystemPayloadSchema>

export const conversationListItemDtoSchema = z.object({
  id: z.string().uuid(),
  otherUser: dmParticipantDtoSchema,
  lastMessageContent: z.string().nullable(),
  lastMessageKind: z.string().nullable(),
  /** Set only when the last message is a `system` event; localize it client-side. */
  lastMessageSystem: dmSystemPayloadSchema.nullable(),
  lastMessageAt: z.string().nullable(),
  lastMessageSenderId: z.string().uuid().nullable(),
  /** Saturates at 100 — a value of 100 means "100 or more", render it as "99+" if you prefer. */
  unreadCount: z.number().int(),
  muted: z.boolean(),
})

export type ConversationListItemDto = z.infer<typeof conversationListItemDtoSchema>

export const conversationListResponseSchema = z.object({
  data: z.array(conversationListItemDtoSchema),
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  }),
})

export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>

export const createConversationInputSchema = z.object({
  recipientId: z.string().uuid(),
  message: z.string().min(1).max(2000).optional(),
})

export type CreateConversationInput = z.infer<typeof createConversationInputSchema>

export const createConversationResponseSchema = z.object({
  conversationId: z.string().uuid(),
})

export type CreateConversationResponse = z.infer<typeof createConversationResponseSchema>

export const conversationDetailDtoSchema = z.object({
  id: z.string().uuid(),
  otherUser: dmParticipantDtoSchema,
  muted: z.boolean(),
  /** Announcements-only conversation: only the official sender may post. */
  readOnly: z.boolean(),
  otherReadAt: z.string().nullable(),
  /**
   * The viewer's own read pointer, as it stood before this request. The unread
   * divider anchors at the oldest incoming message newer than it; null when the
   * viewer has never read this conversation.
   */
  myReadAt: z.string().nullable(),
  disappearingTtlSeconds: z.number().int().nullable(),
  /** One of the two ended this chat while they are not friends: nobody can write in it. */
  ended: z.boolean().optional(),
})

export type ConversationDetailDto = z.infer<typeof conversationDetailDtoSchema>

export const markReadInputSchema = z.object({
  messageId: z.string().uuid().optional(),
})

export type MarkReadInput = z.infer<typeof markReadInputSchema>

/** Response when consuming a view-once photo — the one-time signed image URL. */
export const consumeViewResponseSchema = z.object({
  imageUrl: z.string(),
})

export type ConsumeViewResponse = z.infer<typeof consumeViewResponseSchema>

export const setMutedInputSchema = z.object({
  muted: z.boolean(),
})

export type SetMutedInput = z.infer<typeof setMutedInputSchema>

/** Ephemeral typing signal — broadcast to the conversation channel, never stored. */
export const typingInputSchema = z.object({
  isTyping: z.boolean(),
})

export type TypingInput = z.infer<typeof typingInputSchema>

/** Set (or clear, with null) the 24h-disappearing TTL on a chat. Shared by DM + plan chat. */
export const setDisappearingInputSchema = z.object({
  ttlSeconds: z.number().int().nullable(),
})

export type SetDisappearingInput = z.infer<typeof setDisappearingInputSchema>

/** An upcoming plan both people of a 1:1 chat are in or invited to. */
export const sharedPlanDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  date: z.string().nullable(),
  timeStart: z.string().nullable(),
  allDay: z.boolean(),
  chatVisible: z.boolean(),
})

export type SharedPlanDto = z.infer<typeof sharedPlanDtoSchema>

export const sharedPlansResponseSchema = z.object({
  plans: z.array(sharedPlanDtoSchema),
})

/** The invite an event card carries: open until the friend says "Bin dabei", then their plan. */
export const eventInviteSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('open') }),
  z.object({ status: z.literal('accepted'), planId: z.string() }),
])

export type EventInvite = z.infer<typeof eventInviteSchema>

/** The invite on an event card's metadata; null for a plain shared event. */
export function eventInviteOf(metadata: unknown): EventInvite | null {
  if (typeof metadata !== 'object' || metadata === null) return null
  const parsed = eventInviteSchema.safeParse((metadata as { invite?: unknown }).invite)
  return parsed.success ? parsed.data : null
}
